import React, { useEffect, useMemo, useState } from 'react'
import {
  STAT_KEYS,
  RANDOM_POINTS,
  MANUAL_POINTS,
  RANDOM_STAT_MIN,
  RANDOM_STAT_MAX,
  INITIAL_REROLLS,
  emptyStats,
  rollRandomStats,
  sumStats,
  mergeStats,
  validateAllocation,
  setManualStat,
} from './statAllocation'
import { computeDerivedPVCK } from './characterDerivedStats'
import './StatAllocationPanel.css'

/**
 * UI compartida: roll 20 + 30 manuales (1 reroll).
 */
export default function StatAllocationPanel({
  level = 10,
  title = 'Atributos',
  subtitle = null,
  onValidChange,
  footer = null,
}) {
  const [randomStats, setRandomStats] = useState(() => rollRandomStats())
  const [manualStats, setManualStats] = useState(() => emptyStats())
  const [rerollsLeft, setRerollsLeft] = useState(INITIAL_REROLLS)
  const [errors, setErrors] = useState([])

  const stats = useMemo(() => mergeStats(randomStats, manualStats), [randomStats, manualStats])
  const manualUsed = useMemo(() => sumStats(manualStats), [manualStats])
  const manualLeft = MANUAL_POINTS - manualUsed
  const { PV, CK } = useMemo(() => computeDerivedPVCK(stats, level, 0, 0), [stats, level])

  useEffect(() => {
    const errs = validateAllocation(randomStats, manualStats)
    setErrors(errs)
    onValidChange?.({
      valid: errs.length === 0,
      stats,
      randomStats,
      manualStats,
      errors: errs,
      PV,
      CK,
    })
  }, [randomStats, manualStats, stats, PV, CK, onValidChange])

  const reroll = () => {
    if (rerollsLeft <= 0) return
    setRandomStats(rollRandomStats())
    setManualStats(emptyStats())
    setRerollsLeft((n) => n - 1)
  }

  const onManualChange = (key, val) => {
    setManualStats((cur) => setManualStat(cur, key, val, MANUAL_POINTS))
  }

  const defaultSubtitle = `Base aleatoria de ${RANDOM_POINTS} puntos (entre ${RANDOM_STAT_MIN} y ${RANDOM_STAT_MAX} por atributo) + ${MANUAL_POINTS} a repartir (total ${RANDOM_POINTS + MANUAL_POINTS}).`

  return (
    <div className="stat-alloc">
      <header className="stat-alloc__head">
        <h3 className="stat-alloc__title">{title}</h3>
        <p className="stat-alloc__sub">{subtitle || defaultSubtitle}</p>
      </header>

      <div className={`stat-alloc__points${manualLeft === 0 ? ' stat-alloc__points--ok' : ''}`}>
        <span>
          Base aleatoria: <strong>{RANDOM_POINTS}</strong>
        </span>
        <span>
          Puntos libres: <strong>{manualLeft}</strong> / {MANUAL_POINTS}
        </span>
        {rerollsLeft > 0 ? (
          <button type="button" className="stat-alloc__reroll" onClick={reroll}>
            Volver a tirar ({rerollsLeft})
          </button>
        ) : (
          <span className="stat-alloc__reroll-done">Sin más tiradas</span>
        )}
      </div>

      {errors.length > 0 ? (
        <ul className="stat-alloc__errors" role="alert">
          {errors.map((e) => (
            <li key={e}>{e}</li>
          ))}
        </ul>
      ) : null}

      <div className="stat-alloc__rows">
        {STAT_KEYS.map(({ key, label }) => {
          const base = randomStats[key] || 0
          const manual = manualStats[key] || 0
          const total = stats[key] || 0
          return (
            <div key={key} className="stat-alloc__row">
              <div className="stat-alloc__label">
                <strong>{label}</strong>
                <span>
                  {total} <em>({base} + {manual})</em>
                </span>
              </div>
              <input
                type="range"
                min={0}
                max={Math.max(manual + manualLeft, manual, 1)}
                step={1}
                value={manual}
                onChange={(e) => onManualChange(key, e.target.value)}
                aria-label={`${label} puntos manuales`}
              />
              <input
                type="number"
                min={0}
                step={1}
                value={manual}
                onChange={(e) => onManualChange(key, e.target.value)}
                className="stat-alloc__num"
                aria-label={`${label} valor manual`}
              />
            </div>
          )
        })}
      </div>

      <div className="stat-alloc__derived">
        <div>
          <h4>PV</h4>
          <p>
            ({stats.resistencia}*5) + ({stats.fuerza}*2) + ({level}*3) = <strong>{PV}</strong>
          </p>
        </div>
        <div>
          <h4>CK</h4>
          <p>
            ({stats.controlChakra}*8) + ({stats.voluntad}*3) + ({level}*2) = <strong>{CK}</strong>
          </p>
        </div>
      </div>

      {footer}
    </div>
  )
}
